import React, { useEffect } from "react";
import { Modal, Form, Input, InputNumber, Switch } from "antd";
import { notify } from "../../../utils/toast";
import { AxiosError } from "axios";
import {
  useCreateMeetingRoom,
  useUpdateMeetingRoom,
} from "../../../query/HrmQuery";
import { ICreateMeetingRoom } from "../../../models/Hrm";

interface Props {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  initialData?: any | null;
}

const ModalAddMeetingRoom: React.FC<Props> = ({
  open,
  onCancel,
  onSuccess,
  initialData,
}) => {
  const [form] = Form.useForm();
  const createMutation = useCreateMeetingRoom();
  const updateMutation = useUpdateMeetingRoom();

  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue(initialData);
      } else {
        form.resetFields();
      }
    }
  }, [open, initialData, form]);

  const onFinish = async (values: ICreateMeetingRoom) => {
    try {
      if (initialData) {
        await updateMutation.mutateAsync({ id: initialData.id, data: values });
        notify.success("Cập nhật phòng họp thành công");
      } else {
        await createMutation.mutateAsync(values);
        notify.success("Tạo phòng họp thành công");
      }
      onSuccess();
    } catch (error: unknown) {
      const err = error as AxiosError<{ message: string }>;
      notify.error(
        err.response?.data?.message ||
          (initialData ? "Cập nhật thất bại" : "Tạo thất bại"),
      );
    }
  };

  return (
    <Modal
      title={initialData ? "Cập nhật phòng họp" : "Thêm phòng họp mới"}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={createMutation.isPending || updateMutation.isPending}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ isAvailable: true }}
      >
        <Form.Item
          name="name"
          label="Tên phòng"
          rules={[
            { required: true, message: "Vui lòng nhập tên phòng" },
            {
              whitespace: true,
              message: "Tên phòng không được chỉ chứa khoảng trắng",
            },
            { max: 100, message: "Tên phòng tối đa 100 ký tự" },
          ]}
        >
          <Input placeholder="Ví dụ: Phòng họp A" />
        </Form.Item>
        <Form.Item
          name="floor"
          label="Tầng"
          rules={[
            { required: true, message: "Vui lòng nhập tầng" },
            {
              whitespace: true,
              message: "Tầng không được chỉ chứa khoảng trắng",
            },
            { max: 50, message: "Tầng tối đa 50 ký tự" },
          ]}
        >
          <Input placeholder="Ví dụ: Tầng 2" />
        </Form.Item>
        <Form.Item
          name="location"
          label="Vị trí"
          rules={[
            { required: true, message: "Vui lòng nhập vị trí" },
            {
              whitespace: true,
              message: "Vị trí không được chỉ chứa khoảng trắng",
            },
            { max: 200, message: "Vị trí tối đa 200 ký tự" },
          ]}
        >
          <Input placeholder="Ví dụ: Hà Nội" />
        </Form.Item>
        <Form.Item
          name="capacity"
          label="Sức chứa (người)"
          rules={[
            { required: true, message: "Vui lòng nhập sức chứa" },
            {
              type: "number",
              min: 1,
              message: "Sức chứa tối thiểu là 1 người",
            },
            {
              type: "number",
              max: 1000,
              message: "Sức chứa tối đa là 1000 người",
            },
          ]}
        >
          <InputNumber
            min={1}
            max={1000}
            className="w-full"
            placeholder="Ví dụ: 10"
          />
        </Form.Item>
        <Form.Item
          name="isAvailable"
          label="Trạng thái hoạt động"
          valuePropName="checked"
          rules={[{ required: true, message: "Vui lòng chọn trạng thái" }]}
        >
          <Switch
            checkedChildren="Đang hoạt động"
            unCheckedChildren="Tạm ngưng"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ModalAddMeetingRoom;
